import { useCallback, useEffect, useMemo, useState } from 'react';
import { AddCircle, RemoveCircle } from '@mui/icons-material';
import { ButtonGroup, FormControl, IconButton, Stack, Table, TableBody, TableHead, TextField, Tooltip, Typography } from '@mui/material';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import { CellContext, RowData, TableMeta, createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { WritableAtom, atom, useAtom } from 'jotai';
import { SafetyPredictionTrigger, SafetyPredictionType, Threshold, ThresholdsByTrigger, Unit } from '@/api/management/requests';
import { ColorPickerPopover } from '@/components/common/PopOver/ColorPickerPopover';
import Select from '@/components/common/Select/Select';
import { thresholdsConfigurationAtom } from '@/features/Admin/OrganisationManagement/ConfigureGraph/ConfigureGraphForOrganisation';

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    updateData: (rowIndex: number, columnId: string, value: TData[keyof TData]) => void;
    addNewRow: (rowIndex: number) => void;
    removeRow: (rowIndex: number) => void;
  }
}

type ThresholdProps = Threshold[keyof Threshold];
type ThresholdCellContextProps<TValue> = {
  context: CellContext<Threshold, TValue>;
};

const thresholdsByTriggerAtom: WritableAtom<(t: SafetyPredictionTrigger) => Threshold[], ThresholdsByTrigger[], void> = atom(
  (get) => (trigger: SafetyPredictionTrigger) =>
    get(thresholdsConfigurationAtom)
      .filter((t) => t.trigger == trigger)
      .flatMap((t) => t.thresholds),

  (_, set, thresholdsByTrigger) => {
    set(thresholdsConfigurationAtom, (prev) => {
      const unmodifiedThresholds = prev.filter((t) => t.trigger != thresholdsByTrigger.trigger);
      return [...unmodifiedThresholds, thresholdsByTrigger];
    });
  }
);

const unitDisplayFn = (unit: Unit) => {
  switch (unit) {
    case Unit.DEG:
      return '°';
    case Unit.M:
      return 'm';
    case Unit.K_N:
      return 'kN';
    case Unit.TONNES:
      return 'tonnes';
  }
};

function useInputCell<TValue extends ThresholdProps>({ getValue, row: { index }, column: { id }, table }: CellContext<Threshold, TValue>) {
  const initialValue = getValue();
  // We need to keep and update the state of the cell normally
  const [value, setValue] = useState(initialValue);

  // When the input is blurred, we'll call our table meta's updateData function
  const onBlur = () => {
    table.options.meta?.updateData(index, id, value);
  };

  // If the initialValue is changed external, sync it up with our state
  useEffect(() => {
    setValue(() => initialValue);
  }, [initialValue]);

  return {
    value,
    setValue,
    onBlur,
  };
}

function ThresholdInputCell<TValue extends ThresholdProps>({
  context,
  label,
  type,
  required,
  style,
}: ThresholdCellContextProps<TValue> & TextFieldProps) {
  const { value, setValue, onBlur } = useInputCell(context);

  return (
    <FormControl size={'small'}>
      <TextField
        label={label}
        type={type}
        value={(value as TValue) ?? ''}
        required={required}
        onChange={(e) => setValue(e.target.value as TValue)}
        variant={'standard'}
        onBlur={onBlur}
        style={style}
      />
    </FormControl>
  );
}

function ColorSelectionCell({ context }: ThresholdCellContextProps<string>) {
  const { value, setValue, onBlur } = useInputCell(context);

  return (
    <FormControl>
      <ColorPickerPopover color={value} onChange={setValue} onClose={onBlur} />
    </FormControl>
  );
}

const predictionTypeDisplayFn = (spt: SafetyPredictionType) => [...spt.split('_').slice(1)].join(' ');

function ThresholdTypeSelectionCell({
  context,
  onSafetyPredictionTypeChanged,
}: {
  context: CellContext<Threshold, SafetyPredictionType>;
  onSafetyPredictionTypeChanged: (_: SafetyPredictionType) => void;
}) {
  const [value, setValue] = useState(context.getValue() as SafetyPredictionType);
  const options = [SafetyPredictionType.ABOVE_UPPER_LIMIT, SafetyPredictionType.ABOVE_LOWER_LIMIT];

  const onChange = (type: SafetyPredictionType) => {
    if (value != type) {
      setValue(type);
      onSafetyPredictionTypeChanged(type);
    }
  };

  return (
    <Select
      label={'Safety Prediction Level'}
      options={options}
      displayFunction={predictionTypeDisplayFn}
      onChange={onChange}
      value={value}
      size={'small'}
    />
  );
}

function UnitSelection({ initValue, onUnitSelected }: { initValue: Unit; onUnitSelected: (_: Unit) => void }) {
  const [value, setValue] = useState(initValue);
  const unitSelectOptions = [Unit.K_N, Unit.TONNES];

  const onChange = useCallback(
    (unit: Unit) => {
      setValue(unit);
      onUnitSelected(unit);
    },
    [setValue, onUnitSelected]
  );

  return (
    <Select
      label={'Unit'}
      name={'unit'}
      options={unitSelectOptions}
      displayFunction={unitDisplayFn}
      onChange={onChange}
      value={value}
      size={'small'}
    />
  );
}

const motionTriggers = [
  SafetyPredictionTrigger.MOTION_SURGE,
  SafetyPredictionTrigger.MOTION_SWAY,
  SafetyPredictionTrigger.MOTION_YAW,
  SafetyPredictionTrigger.MOTION_HEAVE,
  SafetyPredictionTrigger.MOTION_ROLL,
  SafetyPredictionTrigger.MOTION_PITCH,
];

const thresholdColumnHelper = createColumnHelper<Threshold>();

function ThresholdsEditableTable({ trigger }: { trigger: SafetyPredictionTrigger }) {
  const [getThresholds, setThresholds] = useAtom(thresholdsByTriggerAtom);
  const thresholds = useMemo(() => getThresholds(trigger), [getThresholds, trigger]);
  const onUnitChanged = useCallback(
    (unit: Unit) => {
      setThresholds({
        trigger,
        thresholds: thresholds.map((t) => ({ ...t, unit })),
      });
    },
    [setThresholds, trigger, thresholds]
  );
  const onSafetyPredictionTypeChanged = useCallback(
    (index: number) => (safetyPredictionType: SafetyPredictionType) => {
      setThresholds({
        trigger,
        thresholds: thresholds.map((t, idx) => (idx == index ? { ...t, safetyPredictionType } : t)),
      });
    },
    [setThresholds, trigger, thresholds]
  );

  const columns = useMemo(
    () => [
      thresholdColumnHelper.accessor('label', {
        header: () => (
          <Tooltip title={'This is the primary indicator of which threshold being used'} placement={'top'}>
            <Typography variant={'h6'}>Label</Typography>
          </Tooltip>
        ),
        cell: (context) => <ThresholdInputCell context={context} required={true} type={'text'} style={{ width: 200 }} />,
      }),
      thresholdColumnHelper.accessor('abbr', {
        header: () => (
          <Tooltip title={'The abbr of the label, used mostly in compact spaces, e.g. graph legends or tables'} placement={'top'}>
            <Typography variant={'h6'}>Abbr.</Typography>
          </Tooltip>
        ),
        cell: (context) => <ThresholdInputCell context={context} required={false} type={'text'} style={{ width: 80 }} />,
      }),
      thresholdColumnHelper.accessor('description', {
        header: () => (
          <Tooltip title={'The full description of the threshold, mostly used as tooltips'} placement={'top'}>
            <Typography variant={'h6'}>Description</Typography>
          </Tooltip>
        ),
        cell: (context) => <ThresholdInputCell context={context} required={false} type={'text'} style={{ width: 280 }} />,
      }),
      thresholdColumnHelper.accessor('percentage', {
        header: motionTriggers.includes(trigger) ? '' : 'Threshold %',
        size: 50,
        cell: (context) => {
          if (motionTriggers.includes(trigger)) {
            return <div style={{ width: 90 }}></div>;
          }
          return <ThresholdInputCell context={context} required={true} type={'number'} style={{ width: 50 }} />;
        },
      }),
      thresholdColumnHelper.accessor('color', {
        header: 'Threshold Color',
        cell: (context) => <ColorSelectionCell context={context} />,
      }),
      thresholdColumnHelper.accessor('unit', {
        header: 'Threshold Unit',
        cell: (context) => {
          if ([SafetyPredictionTrigger.LINE_FORCE, SafetyPredictionTrigger.BOLLARD_FORCE].includes(trigger) && context.row.index == 0) {
            return <UnitSelection initValue={context.getValue()} onUnitSelected={onUnitChanged} />;
          }
          return context.renderValue();
        },
      }),
      thresholdColumnHelper.accessor('safetyPredictionType', {
        header: 'Threshold Level',
        size: 120,
        cell: (context) => {
          return (
            <ThresholdTypeSelectionCell
              context={context}
              onSafetyPredictionTypeChanged={onSafetyPredictionTypeChanged(context.row.index)}
            />
          );
        },
      }),
      thresholdColumnHelper.display({
        id: ' action-row',
        header: '',
        cell: ({ row, table }) => {
          const rows = table.getRowModel().rows;
          return (
            <ButtonGroup size={'small'}>
              <IconButton
                color={'primary'}
                onClick={() => table.options.meta.addNewRow(row.index + 1)}
                disabled={motionTriggers.includes(trigger) && rows.length >= 2}
              >
                <AddCircle />
              </IconButton>
              <IconButton
                color={'default'}
                onClick={() => table.options.meta.removeRow(row.index)}
                // Prevent removal of rows with SafetyPredictionType.ABOVE_UPPER_LIMIT as per business rules.
                disabled={row.original.safetyPredictionType == SafetyPredictionType.ABOVE_UPPER_LIMIT || rows.length < 2}
              >
                <RemoveCircle />
              </IconButton>
            </ButtonGroup>
          );
        },
      }),
    ],
    [onUnitChanged, onSafetyPredictionTypeChanged, trigger]
  );

  const tableMetadata: TableMeta<Threshold> = useMemo(
    () => ({
      addNewRow: (rowIndex: number) => {
        const placeholder: Threshold = {
          label: '',
          abbr: '',
          unit: thresholds[rowIndex - 1].unit,
          color: '#e99c00',
          percentage: 0,
          safetyPredictionType: SafetyPredictionType.ABOVE_LOWER_LIMIT,
        };
        setThresholds({
          trigger,
          thresholds: [...thresholds.slice(0, rowIndex), placeholder, ...thresholds.slice(rowIndex)],
        });
      },
      removeRow: (rowIndex: number) => {
        setThresholds({
          trigger,
          thresholds: thresholds.filter((_, idx) => idx != rowIndex),
        });
      },
      updateData: (rowIndex: number, columnId: string, value: ThresholdProps) => {
        const updatedThreshold = {
          ...thresholds[rowIndex]!,
          [columnId]: value,
        };
        setThresholds({
          trigger,
          thresholds: [...thresholds.slice(0, rowIndex), updatedThreshold, ...thresholds.slice(rowIndex + 1)],
        });
      },
    }),
    [thresholds, setThresholds, trigger]
  );
  const table = useReactTable({
    data: thresholds ?? [],
    enableFilters: false,
    getCoreRowModel: getCoreRowModel(),
    columns,
    meta: tableMetadata,
  });

  return (
    <Stack minWidth={1000} maxWidth={1200}>
      <Stack width={280} display={'flex'} direction={'row'} justifyContent={'space-between'}>
        <Typography variant={'h3'}>{trigger.replace('_', ' ')}</Typography>
      </Stack>
      <Table aria-label="thresholds editable table" border={0} size={'small'}>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup, index) => (
            <TableRow key={`${trigger}-${headerGroup.id}-${index}`} sx={{ border: 0 }}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} colSpan={header.colSpan} align={'left'}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row, index) => (
            <TableRow key={`${trigger}-${row.id}-${index}`}>
              {row.getVisibleCells().map((cell) => {
                return (
                  <TableCell sx={{ px: 0, border: 0 }} key={`${trigger}-${row.id}-${cell.id}`} align={'left'}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <br />
    </Stack>
  );
}

export default ThresholdsEditableTable;
